
import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Globe, FileCheck, ArrowRight, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SERVICES } from '../constants';

const Services: React.FC = () => {
  return (
    <section id="services" className="py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-24 gap-8">
          <div className="max-w-2xl text-left">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-[11px] font-black uppercase tracking-[0.2em] mb-6 shadow-sm"
            >
              Institutional Modules
            </motion.div>
            <h2 className="font-display font-black text-4xl md:text-6xl text-slate-900 mb-8 tracking-tight leading-tight">Elite Financial <br /><span className="text-slate-500">Infrastructure</span></h2>
            <p className="text-slate-600 text-xl leading-relaxed font-medium">
              We deploy surgical-grade accounting and compliance modules designed for high-resolution financial stewardship.
            </p>
          </div>
          <Link to="/#contact" className="group px-8 py-4 bg-white border border-slate-200 rounded-2xl text-slate-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all inline-flex items-center gap-3 font-bold shadow-sm hover:shadow-xl">
            Explore All Capabilities
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Bento Box Grid - Light Mode */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-8 group relative"
          >
            <Link to={`/service/${SERVICES[0].id}`} className="block h-full p-12 rounded-[3.5rem] bg-slate-50 border border-slate-200 hover:border-indigo-200 hover:bg-white hover:shadow-2xl transition-all overflow-hidden">
              <div className="absolute top-0 right-0 p-12 text-indigo-50 group-hover:text-indigo-100 transition-colors">
                 <ShieldCheck size={200} />
              </div>
              <div className="relative z-10 flex flex-col h-full">
                <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center text-white mb-10 shadow-lg ring-4 ring-indigo-50">
                   <ShieldCheck size={32} />
                </div>
                <h3 className="text-4xl font-display font-black text-slate-900 mb-6 tracking-tight">{SERVICES[0].title}</h3>
                <p className="text-slate-500 text-xl leading-relaxed max-w-md mb-12">{SERVICES[0].shortDesc}</p>
                <div className="mt-auto flex items-center gap-4 text-indigo-600 font-black uppercase tracking-widest text-[11px]">
                   <span>Deploy Indian Module</span>
                   <ArrowRight size={16} />
                </div>
              </div>
            </Link>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-4"
          >
            <Link to={`/service/${SERVICES[1].id}`} className="block h-full p-10 rounded-[3.5rem] bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20 group">
               <div className="flex flex-col h-full">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mb-10 group-hover:rotate-6 transition-transform">
                     <Globe size={28} />
                  </div>
                  <h3 className="text-3xl font-display font-bold mb-6 tracking-tight leading-tight">{SERVICES[1].title}</h3>
                  <p className="text-indigo-100 opacity-80 leading-relaxed mb-10">{SERVICES[1].shortDesc}</p>
                  <div className="mt-auto flex items-center gap-4 font-black uppercase tracking-widest text-[10px]">
                     <span>Initialize Global Sync</span>
                     <ArrowRight size={16} />
                  </div>
               </div>
            </Link>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-12"
          >
            <Link to={`/service/${SERVICES[2].id}`} className="block p-12 rounded-[3.5rem] bg-slate-50 border border-slate-200 hover:border-indigo-200 hover:bg-white hover:shadow-2xl transition-all group overflow-hidden">
               <div className="flex flex-col md:flex-row gap-12 items-center">
                  <div className="w-20 h-20 rounded-3xl bg-white border border-slate-200 flex items-center justify-center text-indigo-600 shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                     <FileCheck size={40} />
                  </div>
                  <div className="flex-grow">
                     <h3 className="text-3xl font-display font-black text-slate-900 mb-3 tracking-tight">{SERVICES[2].title}</h3>
                     <p className="text-slate-500 text-lg leading-relaxed max-w-3xl">{SERVICES[2].shortDesc}</p>
                  </div>
                  <div className="px-10 py-5 bg-indigo-50 border border-indigo-100 rounded-2xl text-indigo-600 font-bold hover:bg-indigo-600 hover:text-white transition-all shadow-sm">
                     View UAE Perimeter
                  </div>
               </div>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
